import { ProductSlug } from "@/api/product-api";
import { SUPPORT_HUB_URL } from "@/constants/Urls.ts";
import type { LanguageCode } from "@/utils/LocaleUtil";
import { SUPPORTED_GAMES_HREF } from "@/v2-components/pages/games/GamesSubNav";

/**
 * ★**GNB 게임 패널이 카테고리 목록 → sub-nav 3항목으로 단순화됐다** (#5415).
 *
 * 시안이 데스크톱·모바일 **셋 다** `Overview` · `지원 게임 전체` · `Software` 다
 * (`29212:35874` PC · `29212:35926` VR · `29212:36190` 모바일). 그래서 그룹을 만들던
 * `getGamesMenusByCategory` · `PC/VR_GAMES_CATEGORIES` · 메뉴의 `categories` 필드를
 * 걷어냈다 — 호출처가 0곳이 됐다.
 *
 * ⚠️ **`GAMES_SUBMENUS` 와 `category` 필드는 남긴다** — `subMenus` 를 통해 모바일
 * 시트의 다른 경로(`menu-view.tsx:207`)가 아직 읽는다. tsc 가 이걸 잡아서 한 번
 * 과하게 지웠다가 되돌렸다.
 *
 * ⚠️ **아래 페이지들이 GNB 진입점을 잃는다.** 고아는 아니지만 진입이 얇아진다:
 *
 *   /games/meta-quest · /games/steam-vr · /games/pico · /games/ps-vr2
 *   /games/sim-racing · /games/ai-powered · /games/audio-to-haptic · /games
 *
 * 남는 진입점은 대부분 `GameTabMenu.tsx`(구 플랫폼 페이지들의 탭 바) 하나뿐이라
 * **이미 그 페이지에 들어간 사람만** 본다. `/games/audio-to-haptic` 만 7곳으로 두껍다.
 * **의도된 단순화로 보고 진행했다**(2026-08-31 결정) — 시안이 세 화면 모두 3항목이고,
 * 안 쓰는 페이지가 섞여 있을 수 있다는 전제도 함께 받았다. 정리 대상이 정해지면
 * 그때 페이지 자체를 처분한다.
 */
export type GamesCategory = "Explore" | "VR" | "PC" | "Audio";
export type BadgeType = "sale" | "new";

/**
 * 1차 메뉴 라벨의 **단일 출처**. 값은 그대로 번역 키로 쓰인다(`t(menu.name)`).
 *
 * ⚠️ 값을 바꾸면 `public/translations/{en,kr,jp,es,fr,cn,de}/gnb.json` 7개를 같이 고쳐야
 * 한다. 누락은 에러가 아니라 **키(=영문 원문)가 그대로 화면에 나가는 조용한 실패**이고,
 * `__tests__/v2-components/gnb-locale-coverage.test.ts` 와 `__tests__/i18n/
 * locale-key-parity.test.ts`(ratchet)가 그 균일성을 잠근다.
 */
export const MENU_NAME = {
  STORE: "Store",
  PC_GAMES: "PC Games",
  VR_GAMES: "VR Games",
  STREAMER: "Streamer",
  DEVELOPER: "Developer",
  SUPPORT: "Support",
} as const;

export type MenuName = (typeof MENU_NAME)[keyof typeof MENU_NAME];

/**
 * 2deps 패널 키. 패널이 **없는** 1차 메뉴(Streamer / Developer)는 `undefined` 다.
 *
 * ⚠️ 분기는 **이름이 아니라 이 키로** 한다. v1 은 데스크탑(`SwitchCase`)과 모바일이 각자
 * 문자열 리터럴(`"Games"` / `"Software"` …)로 메뉴를 식별해서, 이름을 바꾸면 한쪽만 고치고
 * 다른 쪽은 **조용히 아무것도 안 그렸다**. 키를 union 으로 두면 tsc 가 누락을 잡는다.
 */
export type PanelKey = "store" | "pc" | "vr" | "support";

/**
 * 바 안에서 트리거가 놓이는 묶음.
 *
 * 시안(디자인 파일 `8X5fPtMR3n9lVij5MJKaUh`, 컴포넌트 셋 `Target=Desktop` = node
 * `29212:35665`)은 6개를 한 덩어리로 두지 않는다 — **좌 3 은 로고 옆, 우 3 은 아이콘 옆**.
 * 패널 x 좌표가 이를 뒷받침한다: Store·PC·VR 패널의 `cols` 는 `x=362`(좌측 아래),
 * Support 패널 `menu-Support` 는 `x=1431`(우측 아래).
 */
export type MenuGroup = "left" | "right";

export type SubMenu = {
  imageSrc?: string;
  name: string;
  slug: string;
  href: string;
  dotted?: boolean;
  target?: "_blank" | "_self";
  category?: GamesCategory;
  badge?: BadgeType;
};

export type Menu = {
  name: MenuName;
  href: string;
  /** 좌/우 묶음. 트리거의 렌더 위치를 결정한다. */
  group: MenuGroup;
  /** 2deps 패널 키. 없으면 패널이 없는 단순 링크(캐럿도 없다). */
  panel?: PanelKey;
  dotted?: boolean;
  subMenus: SubMenu[];
  color?: string;
  target?: "_blank" | "_self";
  /**
   * 게임 패널이 렌더할 카테고리 그룹. `PC Games` / `VR Games` 가 같은
   * `getGamesMenusByCategory()` 출력을 나눠 쓴다(그룹 **순서**는 그 함수가 결정하고,
   * 여기 나열 순서는 영향이 없다).
   */
  /**
   * `href` 접두사만으로는 활성 판정이 안 되는 추가 경로.
   *
   * ⚠️ PDP 가 `/tactsuit/<slug>` → **`/shop/buy-tactsuit/<slug>`** 로 옮겨서(#5386 계보),
   * `Store`(`/tactsuit`) 접두사로는 제품 상세에서 활성이 안 잡힌다.
   */
  activePrefixes?: string[];
};

const TACTSUIT_SUBMENUS: SubMenu[] = [
  {
    imageSrc: "/images/devices/all-tactsuit@2x.png",
    name: "All TactSuit",
    slug: "all-tactsuit",
    href: `/tactsuit`,
  },
  {
    imageSrc: "/images/devices/fps-bundle.png",
    name: "Bundles",
    slug: "bundles",
    href: `/tactsuit/bundles`,
  },
  {
    imageSrc: "/images/devices/tactsuit-pro.png",
    name: "TactSuit Pro",
    slug: ProductSlug.TACTSUIT_PRO,
    href: `/shop/buy-tactsuit/tactsuit-pro`,
  },
  {
    imageSrc: "/images/devices/tactsuit-air/air(red+white).png",
    name: "TactSuit Air",
    slug: ProductSlug.TACTSUIT_AIR,
    href: `/shop/buy-tactsuit/tactsuit-air`,
  },
  {
    imageSrc: "/images/devices/tactsleeve.png",
    name: "TactSleeve",
    slug: ProductSlug.TACTSLEEVE,
    href: `/shop/buy-tactsuit/tactsleeve`,
  },
  {
    imageSrc: "/images/devices/tactvisor.png",
    name: "TactVisor",
    slug: ProductSlug.TACTVISOR,
    href: `/shop/buy-tactsuit/tactvisor`,
  },
  {
    imageSrc: "/images/devices/tactglove.png",
    name: "TactGlove",
    slug: ProductSlug.TACTGLOVE_DK3,
    href: `/shop/buy-tactsuit/tactglove-dk3`,
  },
  {
    imageSrc: "/images/devices/feet.png",
    name: "Tactosy for Feet",
    slug: ProductSlug.TACTOSY_FOR_FEET,
    href: `/shop/buy-tactsuit/tactosy-for-feet`,
  },
  {
    imageSrc: "/images/devices/wired-audio-to-haptics-kit.png",
    name: "Accessories",
    slug: ProductSlug.TACTSUIT_X_AUDIO_ACCESSORIES,
    href: `/tactsuit/accessories`,
  },
];

export const MOBILE_TACTSUIT_SUBMENUS: SubMenu[] = [
  {
    imageSrc: "/images/devices/fps-bundle.png",
    name: "Bundles",
    slug: "bundles",
    href: `/tactsuit/bundles`,
  },
  {
    imageSrc: "/images/devices/tactsuit-pro.png",
    name: "TactSuit Pro",
    slug: ProductSlug.TACTSUIT_PRO,
    href: `/shop/buy-tactsuit/tactsuit-pro`,
  },
  {
    imageSrc: "/images/devices/tactsuit-air/air(red+white).png",
    name: "TactSuit Air",
    slug: ProductSlug.TACTSUIT_AIR,
    href: `/shop/buy-tactsuit/tactsuit-air`,
  },
  {
    imageSrc: "/images/devices/tactsleeve.png",
    name: "TactSleeve",
    slug: ProductSlug.TACTSLEEVE,
    href: `/shop/buy-tactsuit/tactsleeve`,
  },
  {
    imageSrc: "/images/devices/tactvisor.png",
    name: "TactVisor",
    slug: ProductSlug.TACTVISOR,
    href: `/shop/buy-tactsuit/tactvisor`,
  },
  {
    imageSrc: "/images/devices/tactglove.png",
    name: "TactGlove",
    slug: ProductSlug.TACTGLOVE_DK3,
    href: `/shop/buy-tactsuit/tactglove-dk3`,
  },
  {
    imageSrc: "/images/devices/feet.png",
    name: "Tactosy for Feet",
    slug: ProductSlug.TACTOSY_FOR_FEET,
    href: `/shop/buy-tactsuit/tactosy-for-feet`,
  },
  {
    imageSrc: "/images/devices/wired-audio-to-haptics-kit.png",
    name: "Accessories",
    slug: ProductSlug.TACTSUIT_X_AUDIO_ACCESSORIES,
    href: `/tactsuit/accessories`,
  },
];

/**
 * 이 배열의 나열 순서는 그룹(카테고리) 노출 순서와 무관하다.
 * 그룹 순서는 `getGamesMenusByCategory`의 `categoryOrder`가 결정하고,
 * 이 배열 순서는 같은 `category` 안에서의 항목 순서만 결정한다.
 */
const GAMES_SUBMENUS: SubMenu[] = [
  {
    /**
     * ★**목적지는 `/games/pc` 다 — `/games` 는 페이지 층 리다이렉트 스텁이다** (#5795).
     *
     * #5683 이 `src/pages/games/index.tsx` 를 `→ /games/pc/` 스텁으로 바꿨는데 이 항목이
     * 따라가지 않아, **전 페이지 GNB 에서** 클릭 한 번마다 홉이 하나 더 붙었다
     * (prod 실측 2026-09-17 · `/ko/games/` → 홉 1 → `/ko/games/pc/`).
     *
     * ⚠️ **끝슬래시를 여기 적지 않는다** — `<Link>` 가 경로부 끝슬래시와 로케일 접두를
     * 둘 다 정규화하므로 **표기는 홉에 영향이 없고**(prod 실측: 소스 `href: "/games/pc"`
     * → 앵커 `/ko/games/pc/`), 형제 항목들과 표기를 맞추는 문제다.
     *
     * ⚠️ **「리포는 전부 무슬래시」가 아니다** (#5797 R1 반영 중 실측 정정). 두 표기가
     * 섞여 있고(`href` 리터럴 기준 **표기가 갈린 경로 10 / 고유 52** · 2026-09-17),
     * 슬래시가 **필요한** 자리도 있다 — `href` 를 활성 판정 키로 겸용하는 곳이다.
     * 비교 대상에 따라 요구가 **정반대**다:
     *
     * ```
     * GamesSubNav.tsx:221     router.pathname === tab.href    라우트 패턴 → 무슬래시여야
     * GameTabMenu.tsx:60      router.asPath   === item.href   trailingSlash:true → 슬래시여야
     * ```
     *
     * `getLocalePrefix` 를 손으로 붙이는 **리다이렉트 목적지**(`docs/pitfalls.md` #24)는
     * 또 다른 축이다 — 거기선 슬래시가 **홉을 좌우한다**. 자세히는 `docs/pitfalls.md` #60.
     *
     * ⚠️ `activePrefixes: ["/games"]`(같은 파일 VR Games 항목)는 **좁히지 않는다** —
     * 저건 링크가 아니라 활성 판정용이고, 좁히면 게임 상세에서 활성 메뉴가 사라진다.
     */
    name: "All Games",
    slug: "/",
    href: `/games/pc`,
    category: "Explore",
  },
  {
    name: "Game Keys",
    slug: "game-keys",
    /**
     * 게임 키 판매 목록 — **VR 목록의 `only-sellable` 필터**로 보낸다 (#5648 후속).
     *
     * 종전 목적지 `/games/search?only-sellable=true` 는 renewal_v2 이전 화면이다.
     *
     * ⚠️ 이 근거는 종전에 ~~`DisplayGameKeySales.tsx`~~ docblock 에 한 번만 적고 여기서
     * 가리켰다. #5689 가 그 파일을 지워서(레거시 게임 화면 전용이었다) **여기로 옮겼다** —
     * 같은 목적지를 쓰던 세 자리 중 남은 것이 여기 하나다(나머지: `/shop/buy-tactsuit`
     * See All 은 #5701 이 페이지째 삭제).
     *
     * ## 왜 VR 인가 — PC 목록엔 이 필터가 없다
     *
     * `use-vr-games-filters.ts` 가 `query["only-sellable"]` 을 읽고 칩 토글까지 갖는다.
     * PC 쪽(`use-pc-games-filters.ts`)이 읽는 것은 `support`·`genre`·`q`·`sort` 뿐이다.
     *
     * ## VR 로 보내도 게임 키가 안 빠진다 — **술어로** 확인했다
     *
     * 조건은 「판매 중인 게임 키가 **전부 VR 플랫폼을 갖는가**」다. prod 실측
     * (2026-09-09 · 앱이 쓰는 경로)에서 6건 전부 VR 플랫폼을 가져 **빠지는 것 0건**이었다.
     *
     * ⚠️ **시점 의존이다.** `pc`·`simracing` 만 가진 게임 키가 등록되면 그 순간 여기서
     * 빠진다. 조건은 절대값(6)이 아니라 위 술어이고, 아래 명령으로 다시 잰다.
     * **dev 로 재지 말 것** — 테스트 데이터가 섞여 값이 다르다(dev 14 vs prod 6).
     *
     * ```
     * curl -s "https://sdk-apis.bhaptics.com/api/v2/bhaptics-contents/by-filters?filters=&sort=&only-sellable=true"
     * ```
     */
    href: `${SUPPORTED_GAMES_HREF.vr}/?only-sellable=true`,
    category: "Explore",
    badge: "sale",
  },
  {
    name: "PC Game",
    slug: "pc",
    href: `/games/pc`,
    category: "PC",
  },
  {
    // 플랫폼별 랜딩을 걷어내(#5604) VR 카테고리가 비었다. PC 와 대칭으로 오버뷰를 둔다 —
    // 안 두면 이 시트에 VR 게임 입구가 아예 없어진다(데스크탑 패널은 `getSubNavTabs` 로 있다).
    name: "VR Game",
    slug: "vr",
    href: `/games/vr`,
    category: "VR",
  },
];

export type GamesMenuItem = {
  title: string;
  href: string;
  dotted?: boolean;
  badge?: BadgeType;
};

export type GamesMenuGroup = {
  title: GamesCategory;
  menus: GamesMenuItem[];
};

const GAMES_SUBMENUS_KO = GAMES_SUBMENUS.filter(
  (menu) => menu.slug !== "game-keys",
);

function getGamesSubmenus(locale?: LanguageCode): SubMenu[] {
  return locale === "ko" ? GAMES_SUBMENUS_KO : GAMES_SUBMENUS;
}

const SUPPORT_SUBMENUS: SubMenu[] = [
  {
    name: "Support Hub",
    slug: "help",
    href: SUPPORT_HUB_URL.home,
    target: "_blank",
  },
  {
    /**
     * ★**기본 `type` 을 박는다** (#5795) — 종전 1차 메뉴(`Support`)도 같은 값을 썼다
     * (2026-09-29 1차 메뉴는 Support Hub 로 바뀌었다).
     *
     * `/support/downloads` 는 (2026-09-17 당시) `query.type` 이 없으면 `?type=manual` 로
     * 리다이렉트했다(`src/pages/support/downloads.tsx`) — 지금은 Player 로 간다(#5848).
     * 어느 쪽이든 기본값에 기대지 않고 명시한다. 당시 1차 메뉴는 #5514 에서 이미 쿼리를
     * 붙였는데 **패널 안 이 항목만 안 붙어** 홉을 하나 더 썼다
     * (prod 실측 2026-09-17 · `/ko/support/downloads/` → 홉 1 → `?type=manual`).
     *
     * ⚠️ 1차 메뉴와 달리 여기엔 `activePrefixes` 를 안 둔다 — `SubMenu` 에는 그 필드가
     * 없고, 하위 항목의 활성 판정은 `href` 를 안 쓴다(`slug` 는 `game-keys` 필터 전용).
     */
    name: "Downloads",
    slug: "downloads",
    href: `/support/downloads?type=manual`,
  },
  /* ★`Software` 임시 항목을 뺐다 (#5415).
     그 자리는 「임시 거처」라고 종전 주석이 스스로 밝혔고, 거기 적힌 제거 조건
     (「최종 자리는 PC/VR 게임 패널의 sub-nav 항목」)이 충족됐다 — `GamesSubNav` 가
     PC/VR 각각 `/games/{pc,vr}/software` 를 셋째 탭으로 갖는다(#5453).
     시안 `29212:35927`(2deps-VR-Support)도 항목이 **4개**다: 고객센터 · 다운로드 ·
     About bHaptics · Where to Experience.
    ⚠️ `/software/player` 는 고아가 되지 않는다 — **소스 진입점 11곳**이 남는다
    (2026-09-18 갱신 · 각 줄이 자기 명령을 갖는다):

      0  webview          grep -rc 'software/player' src/components/webview/games/main/
      2  /about           grep -c  'software/player' …/about/_components/SoftwareSection.tsx
      7  블로그 본문       grep -rl 'software/player' content/blog/      (파일 하나 = 로케일 하나)
      2  getting-started  grep -o  'software/player' content/getting-started/getting-started.json

    (`src/json/blog.generated.json` 은 블로그 7건을 미러하는 **빌드 산출물**이라 안 센다.)

    ★한 줄짜리 전역 grep 을 쓰지 않는 이유가 둘이다. ① **앞 슬래시가 선택적**이다 —
    webview 는 `"software/player/?type="` 처럼 상대 경로로 적었어서, 슬래시를 필수로 두면
    그 자리가 통째로 빠졌다. ② 전역 grep 은 **이 주석 자신**을 센다(실측: 32건 중 webview
    가 7로 나왔는데 그 1건이 여기였다). 위 네 글롭에는 이 파일이 없다.

    ★제품 페이지 `BhapticsCompanionSoftware.tsx` 는 **이 목록에서 빠졌다** (#5647) — 그
    파일의 링크 3개가 `/games/{pc,vr}/software/` 직결로 바뀌어 더는 스텁을 거치지 않는다
    (남은 등장은 docstring 인용뿐).

    ★webview 6곳도 **#5804 에서 닫았다** — 같은 「직결」 규약(#5604 R1 M2)이다. 종전엔
    #5647 의 범위 밖이라 남겨 뒀었고, 이제 `/games/{vr,pc}/software/` 직결이라 위 첫 줄이
    **0**이다(그 매핑은 `__tests__/webview/bridge-url-shape.test.ts` 가 잠근다).

     ★`Developer` 와 `Blog` 도 뺐다 — 데스크톱 시안(`29247:21398`)과 모바일 시안
     (`29304:241277`)이 **둘 다 4개**다.
     · `Developer` 는 고아가 아니다 — GNB **1차 메뉴**로 따로 있다(`MENU_NAME.DEVELOPER`,
       아래 `getNavigationMenus`). 시안 바에도 `Streamer · Developer · Support` 다.
       패널 안의 것은 중복이었다.
     · `Blog` 는 이 패널이 **사이트 유일의 진입점**이었다(실측: 렌더된 페이지 전체에서
       `/blog` 링크 1개, 푸터에 없음). 그래서 푸터 `Support` 컬럼으로 옮겼다
       (`footer-service.ts`) — 안 옮기면 내비게이션에서 닿을 길이 사라진다. */
  {
    name: "About bHaptics",
    slug: "about",
    href: `/about`,
  },
  {
    name: "Where to Experience",
    slug: "where-to-experience",
    href: `/business/where-to-try`,
  },
];

/** 게임 패널 2개가 나눠 갖는 카테고리. `Explore`(전체·게임키)는 양쪽 공통. */
export function getNavigationMenus(locale?: LanguageCode): Menu[] {
  return [
    {
      name: MENU_NAME.STORE,
      href: "/tactsuit",
      // PDP 가 `/shop/buy-tactsuit/<slug>` 로 옮겨서 `/tactsuit` 접두사만으로는
      // 제품 상세에서 Store 가 활성이 안 된다.
      activePrefixes: ["/shop/buy-tactsuit"],
      group: "left",
      panel: "store",
      subMenus: TACTSUIT_SUBMENUS,
    },
    {
      name: MENU_NAME.PC_GAMES,
      href: "/games/pc",
      group: "left",
      panel: "pc",
      subMenus: getGamesSubmenus(locale),
    },
    {
      /**
       * ★**`href` 가 Overview 를 가리켜야 한다** (#5514).
       *
       * 종전엔 `/games` 였다. 리뉴얼로 VR Overview 가 `/games/vr` 로 생겼는데
       * 이 값이 따라가지 않았다 — 1차 메뉴가 `<button>` 이라 **이동에 쓰이지 않아**
       * 드러나지 않았고, 클릭 가능하게 만드는 순간 VR 만 엉뚱한 곳으로 간다
       * (실측 2026-09-03: `/games` 는 h1 이 없고, `/games/vr` 이
       * `From seeing the game to feeling the game` 이다).
       *
       * ⚠️ **`href` 는 활성 판정도 겸한다** — `getActiveMenuName` 이 `href` +
       * `activePrefixes` 중 **가장 긴 접두사**를 고른다. `href` 만 좁히면
       * `/games/experiences/<slug>`(게임 상세)와 `/games` 에서 **활성 메뉴가 사라진다.**
       * 그래서 넓은 쪽을 `activePrefixes` 로 옮긴다 — `Store` 가 이미 쓰는 패턴이다.
       *
       * `/games/pc*` 는 PC 의 `href`(`/games/pc`, 길이 9)가 여기(`/games`, 6)보다
       * 길어서 PC 가 이긴다 — 이 접두사가 PC 를 가로채지 않는다.
       */
      name: MENU_NAME.VR_GAMES,
      href: "/games/vr",
      activePrefixes: ["/games"],
      group: "left",
      panel: "vr",
      subMenus: getGamesSubmenus(locale),
    },
    /* 패널 없음(`panel` 미지정) = 데스크탑에서 캐럿 없는 단순 링크, 모바일에서
       `WithoutContent`(아코디언 없이 바로 이동). */
    {
      name: MENU_NAME.STREAMER,
      href: "/software/live",
      group: "right",
      subMenus: [],
    },
    {
      /** `?type=` 기본값 클래스(아래 패널의 Downloads 와 같다) — `?type=sdk` 없으면 리다이렉트한다
          (`src/pages/support/developers.tsx:25`). 이 항목은 #5514 이전부터 링크였으므로
          홉 하나는 `pre-existing` 이지만, 사유가 같아 함께 고친다. */
      name: MENU_NAME.DEVELOPER,
      href: "/support/developers?type=sdk",
      activePrefixes: ["/support/developers"],
      group: "right",
      subMenus: [],
    },
    {
      /**
       * ★**라벨 클릭 = 외부 Support Hub(새 탭)** (2026-09-29 요청: *"support 를 누르면
       * support hub 로 진입해야"*).
       *
       * 종전엔 `/support/downloads?type=manual`(매뉴얼 다운로드)이었다 — #5514 로 라벨이
       * 클릭=이동이 됐을 때 「허브는 외부」라 내부 페이지를 골랐던 것이다. 패널 안 항목
       * (`SUPPORT_SUBMENUS` 의 Support Hub · 푸터)이 이미 같은 주소를 **새 탭**으로 연다 —
       * 1차 메뉴도 그 규약을 따른다.
       *
       * ## `target` 을 둬도 패널은 열린다
       *
       * 종전 주석은 *"`target` 을 두면 새 탭 직행이 되어 패널을 열 수 없다"* 였다 — 라벨이
       * 클릭으로 패널을 토글하던 `<button>` 시절의 사정이다. 지금 데스크톱 패널은 **hover·
       * focus** 로 열리고 클릭은 이동이라(`navigation-menu.tsx`), `target` 은 이동 방식만
       * 바꾼다. 모바일은 Support 를 아코디언(`WithContent`)으로 그려 1차 `href`·`target` 을
       * 쓰지 않는다.
       *
       * ## 활성 판정은 경로로 따로 둔다
       *
       * 외부 `href` 는 어떤 경로와도 매칭되지 않는다. `/support/downloads` 는 패널 안
       * Downloads 의 화면이라 Support 가 굵어지는 것이 맞으므로 `activePrefixes` 를 유지한다.
       */
      name: MENU_NAME.SUPPORT,
      href: SUPPORT_HUB_URL.home,
      target: "_blank",
      activePrefixes: ["/support/downloads"],
      group: "right",
      panel: "support",
      subMenus: SUPPORT_SUBMENUS,
    },
  ];
}

/**
 * 현재 경로에 해당하는 1차 메뉴 이름. 없으면 `undefined`.
 *
 * ⚠️ **단순 `startsWith` 로는 안 된다.** `VR Games`(`/games`)가 `PC Games`(`/games/pc`)의
 * 접두사라서 `/games/pc` 에서 두 메뉴가 **동시에** 굵어진다. 그래서 접두사로 일치하는
 * 후보 중 **가장 긴 것 하나만** 고른다. `/support/downloads`(Support) 와
 * `/support/developers`(Developer) 도 같은 이유로 이 규칙이 필요하다.
 *
 * ⚠️ 판정 입력은 **`asPath`** 다. PDP 는 동적 라우트라 `pathname` 이
 * `/shop/buy-tactsuit/[slug]` 라는 패턴 문자열이어서 접두사 비교가 성립하지 않는다
 * (같은 이유로 `navigation-menu-content.tsx` 도 `asPath` 를 쓴다).
 */
/**
 * 활성 1차 메뉴. 기본은 **가장 긴 접두사**로 고른다.
 *
 * ## ⚠️ 경로로 못 가르는 화면이 있다 — `override` 가 그걸 받는다
 *
 * `VR Games` 의 `href` 가 **`/games`** 라 `/games/*` 를 통째로 잡는다. 형제 `PC Games`
 * 는 `/games/pc`(9자)로 더 길어 그쪽만 이긴다. 그래서 **게임 상세**
 * (`/games/experiences/{slug}`)는 PC 게임이어도 `VR Games` 로 칠해진다 —
 * PC Games 탭에서 들어간 사용자가 VR 이 볼드인 화면을 본다(2026-09-02 실측).
 *
 * 경로를 `/games/{pc,vr}/games/{slug}` 로 쪼개는 안은 별건이다: prod sitemap **359개**
 * 이동 + 301 + canonical + locale prefix(`docs/pitfalls.md` #24). 그리고 PC·VR **겸용
 * 게임 3건**(prod 실측: minecraft · no-mans-sky · resident-evil-requiem)은 쪼개도 URL 이
 * 둘이 되므로 canonical 이 또 필요하다 — 경로만으로는 원리적으로 안 끝난다.
 *
 * 대신 **호출처가 아는 값을 넘긴다.** 상세 페이지는 이미 게임 데이터로 섹션을 계산해
 * `GamesSubNav` 에 넘기고 있었다(그쪽은 맞게 그려졌다) — GNB 만 그 값을 안 받고 있었다.
 *
 * ★그 계산이 전수로 성립한다는 것도 확인했다(prod 379건, 2026-09-02):
 * VR 플랫폼 보유 319 · `pc` 43 · `simracing` 20(전부 단독, 다른 플랫폼 없음) ·
 * **플랫폼이 아예 없는 게임 0건**. 즉 기본값으로 잘못 떨어지는 게임이 없다.
 * (오디오햅틱은 플랫폼 값이 아니다 — 게임 속성이 아니라 Player 기능이라 목록에 없다.)
 */
export function getActiveMenuName(
  menus: Menu[],
  asPath: string,
  override?: MenuName,
): MenuName | undefined {
  if (override) return override;
  const path = asPath.split("?")[0];
  return menus
    .flatMap((menu) =>
      [menu.href, ...(menu.activePrefixes ?? [])]
        .filter((prefix) => path.startsWith(prefix))
        .map((prefix) => ({ name: menu.name, len: prefix.length })),
    )
    .sort((a, b) => b.len - a.len)[0]?.name;
}
